import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useSnackbar} from 'notistack';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';
import {
    LabelwinContact,
    LabelwinCustomer,
    LabelwinSystem,
    customerLabel,
    useCustomerSearch,
} from 'Pages/Tasks/labelwinMasterData';

import {SupportSession, elapsedSeconds, formatDuration} from './supportSessionTypes';

interface Props {
    /** Die Sitzung, deren Daten aufgenommen werden; `null` = Dialog zu. */
    session: SupportSession | null;
    onClose: () => void;
    /** Nach dem Speichern — die Seite übernimmt den neuen Stand. */
    onSaved: (session: SupportSession) => void;
}

/**
 * Die Daten zum Fall aufnehmen, während die Uhr läuft.
 *
 * Gespeichert wird ausdrücklich: was hier steht, ist die Grundlage für Aufgabe, Auftrag und
 * Zeitbuchung beim Abschluss — ein stilles Zwischenspeichern würde verdecken, was tatsächlich
 * festgehalten ist.
 *
 * Kunde und Ansprechpartner kommen aus Labelwin, weil nur damit später ein Auftrag entstehen kann.
 * Ohne Kunde bleibt der Weg „Notiz" oder „Aufgabe".
 */
export const SessionDataDialog: React.FC<Props> = ({session, onClose, onSaved}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [callerName, setCallerName] = useState('');
    const [callerPhone, setCallerPhone] = useState('');
    const [callerMail, setCallerMail] = useState('');
    const [subject, setSubject] = useState('');
    const [notes, setNotes] = useState('');
    const [customer, setCustomer] = useState<LabelwinCustomer | null>(null);
    const [customerTerm, setCustomerTerm] = useState('');
    const [contacts, setContacts] = useState<LabelwinContact[]>([]);
    const [contactId, setContactId] = useState<number | ''>('');
    const [systems, setSystems] = useState<LabelwinSystem[]>([]);
    const [supportId, setSupportId] = useState('');
    const [busy, setBusy] = useState(false);

    const {customers, loading: searching} = useCustomerSearch(context.api, customerTerm);

    useEffect(() => {
        if (session === null) return;
        setCallerName(session.callerName ?? '');
        setCallerPhone(session.callerPhone ?? '');
        setCallerMail(session.callerMail ?? '');
        setSubject(session.subject ?? '');
        setNotes(session.notes ?? '');
        setContactId(session.contactId ?? '');
        setSupportId(session.supportId ?? '');
        // Der Kunde steht als Nummer und Abschrift an der Sitzung; für das Auswahlfeld genügt das.
        setCustomer(session.customerId
            ? {
                customerId: session.customerId,
                name: session.customerName,
                name2: null, street: null, postalCode: null, city: null,
                phone: null, searchTerm: null, debtorNumber: null,
            }
            : null);
        setCustomerTerm('');
    }, [session]);

    // Ansprechpartner und Anlagen des Kunden — beides füllt den Auftrag später von selbst.
    useEffect(() => {
        const customerId = customer?.customerId;
        if (!customerId) {
            setContacts([]);
            setSystems([]);
            return;
        }

        let active = true;
        void Promise.all([
            http.get<LabelwinContact[]>(`/Labelwin/Customers/${customerId}/Contacts`)
                .then((r) => { if (active) setContacts(r.data); })
                .catch(() => { if (active) setContacts([]); }),
            http.get<LabelwinSystem[]>(`/Labelwin/Customers/${customerId}/Systems`)
                .then((r) => { if (active) setSystems(r.data); })
                .catch(() => { if (active) setSystems([]); }),
        ]);

        return () => { active = false; };
    }, [http, customer?.customerId]);

    /** Übernimmt Name, Telefon und Mail aus dem gewählten Ansprechpartner. */
    const adoptContact = (value: number | ''): void => {
        setContactId(value);
        const contact = contacts.find((c) => c.contactId === value);
        if (!contact) return;

        if (contact.name) setCallerName(contact.name);
        if (contact.phone) setCallerPhone(contact.phone);
        if (contact.mail) setCallerMail(contact.mail);
    };

    const save = async (): Promise<void> => {
        if (session === null) return;
        setBusy(true);
        try {
            // Leerer String löscht ein Feld ausdrücklich — deshalb gehen alle Felder mit.
            const r = await http.patch<SupportSession>(`/SupportSessions/${session.id}`, {
                callerName,
                callerPhone,
                callerMail,
                customerId: customer?.customerId ?? 0,
                customerName: customer?.name ?? '',
                contactId: contactId === '' ? 0 : contactId,
                supportId,
                subject,
                notes,
            });
            enqueueSnackbar(t('supportSessions.saved'), {variant: 'success'});
            onSaved(r.data);
            onClose();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('supportSessions.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={session !== null} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('supportSessions.dataTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {session && (
                        <Typography variant="body2" color="text.secondary">
                            {t('supportSessions.runningSince', {
                                time: new Date(session.startedAt).toLocaleTimeString(),
                                duration: formatDuration(elapsedSeconds(session)),
                            })}
                        </Typography>
                    )}

                    <TextField autoFocus fullWidth size="small" label={t('supportSessions.field.subject')}
                               value={subject}
                               slotProps={{htmlInput: {maxLength: 100}}}
                               onChange={(e) => setSubject(e.target.value)}
                               helperText={t('supportSessions.subjectHint')}/>

                    <Autocomplete
                        size="small"
                        options={customers}
                        value={customer}
                        loading={searching}
                        onChange={(_, value) => { setCustomer(value); setContactId(''); setSupportId(''); }}
                        onInputChange={(_, value) => setCustomerTerm(value)}
                        getOptionLabel={customerLabel}
                        isOptionEqualToValue={(a, b) => a.customerId === b.customerId}
                        filterOptions={(options) => options}
                        noOptionsText={t('labelTasks.customerHint')}
                        loadingText={t('labelTasks.searching')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('supportSessions.field.customer')}
                                       helperText={t('supportSessions.customerHint')}/>
                        )}
                    />

                    {contacts.length > 0 && (
                        <TextField select fullWidth size="small" label={t('supportSessions.field.contact')}
                                   value={contactId}
                                   onChange={(e) => adoptContact(
                                       e.target.value === '' ? '' : Number(e.target.value))}
                                   helperText={t('supportSessions.contactHint')}>
                            <MenuItem value="">—</MenuItem>
                            {contacts.map((contact) => (
                                <MenuItem key={contact.contactId} value={contact.contactId}>
                                    {[contact.name, contact.phone].filter(Boolean).join(' · ')}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField fullWidth size="small" label={t('supportSessions.field.callerName')}
                                   value={callerName}
                                   onChange={(e) => setCallerName(e.target.value)}/>
                        <TextField fullWidth size="small" label={t('supportSessions.field.callerPhone')}
                                   value={callerPhone}
                                   onChange={(e) => setCallerPhone(e.target.value)}/>
                    </Stack>

                    <TextField fullWidth size="small" label={t('supportSessions.field.callerMail')}
                               value={callerMail}
                               onChange={(e) => setCallerMail(e.target.value)}/>

                    {systems.length > 0 && (
                        <TextField select fullWidth size="small" label={t('supportSessions.field.system')}
                                   value={supportId}
                                   onChange={(e) => setSupportId(e.target.value)}>
                            <MenuItem value="">{t('labelTasks.noSystem')}</MenuItem>
                            {systems.filter((system) => system.supportId).map((system) => (
                                <MenuItem key={system.systemId} value={system.supportId!}>
                                    {[system.name, system.supportId].filter(Boolean).join(' · ')}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <TextField fullWidth size="small" label={t('supportSessions.field.notes')}
                               multiline minRows={4}
                               value={notes}
                               onChange={(e) => setNotes(e.target.value)}
                               helperText={t('supportSessions.notesHint')}/>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('supportSessions.cancel')}</Button>
                <Button variant="contained" onClick={() => void save()} disabled={busy}>
                    {t('supportSessions.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionDataDialog;
